"use client";

import { flags } from "@/components/flags";
import { liveCountries } from "@/config/markets";
import type { LocaleType } from "@/constants/locales";
import { useTranslations } from "next-intl";
import pendingSearch from "@/lib/pendingSearch";
import {
  getCountryDefaultArea,
  getCountryLocale,
  getLocaleCountry,
  type CountryCode,
} from "@/utils/location";
import { buildDiscoverUrl } from "@/app/[locale]/_lib/urlBuilders";
import { ChevronDown } from "lucide-react";
import { useRouter } from "next/navigation";
import { useMemo, useState, startTransition } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

type RegionSelectorProps = {
  currentLocale: LocaleType;
};

function CountryFlag({ countryCode }: { countryCode: CountryCode }) {
  const Flag = flags[countryCode];
  return <Flag />;
}

export function RegionSelector({ currentLocale }: RegionSelectorProps) {
  const t = useTranslations();
  const router = useRouter();
  const [open, setOpen] = useState(false);

  const currentCountry = getLocaleCountry(currentLocale);
  const countryNames = useMemo(
    () => new Intl.DisplayNames(currentLocale, { type: "region" }),
    [currentLocale],
  );

  const handleCountrySelect = (countryCode: CountryCode) => {
    setOpen(false);

    if (countryCode === currentCountry) {
      return;
    }

    // Clear pending search state when country changes
    pendingSearch.clear();

    const targetLocale = getCountryLocale(countryCode);
    const targetArea = getCountryDefaultArea(countryCode);
    startTransition(() => {
      router.push(
        buildDiscoverUrl({ locale: targetLocale, locationSlug: targetArea }),
      );
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 h-10 px-4 rounded-full border border-gray-200 bg-white shadow-sm hover:bg-gray-50 transition-colors"
          aria-label={t("regionSelector.label")}
        >
          <CountryFlag countryCode={currentCountry} />
          <ChevronDown className="size-5 text-gray-600" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-48 p-2"
        align="start"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-1">
          {liveCountries.map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => handleCountrySelect(code)}
              className="flex items-center gap-3 w-full px-3 py-2 rounded-md text-left hover:bg-gray-100 transition-colors"
            >
              <CountryFlag countryCode={code} />
              <span className="text-sm font-medium">
                {countryNames.of(code)}
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
